// True only in the iOS build (`--mode ios`, client/.env.ios). That build opens
// the native share sheet through @capacitor/share.
//
// Defined here and not in config.ts: Rolldown only leaves the Capacitor chunk
// out of the website build when this constant sits in the same module as the
// dynamic import below.
const NATIVE = import.meta.env.VITE_NATIVE === 'true'

// Resolves to the module, not the plugin: see token.ts.
const plugin = () => import('@capacitor/share')

// Phones and tablets get the share sheet. Desktops copy instead, even where
// the browser has a share sheet.
export const sharesViaSheet = (): boolean =>
	NATIVE ||
	('share' in navigator && window.matchMedia('(pointer: coarse)').matches)

export type ShareOutcome = 'shared' | 'copied' | 'failed'

// Backing out of the share sheet counts as shared: there's nothing to report.
export const shareText = async (text: string): Promise<ShareOutcome> => {
	if (NATIVE) {
		const { Share } = await plugin()
		await Share.share({ text }).catch(() => {})
		return 'shared'
	}
	if (sharesViaSheet()) {
		try {
			await navigator.share({ text })
			return 'shared'
		} catch (e) {
			if (e instanceof DOMException && e.name === 'AbortError') {
				return 'shared'
			}
		}
	}
	try {
		await navigator.clipboard.writeText(text)
		return 'copied'
	} catch {
		return 'failed'
	}
}
